/**
 * Shared utility for handling Server-Sent Events (SSE) streaming
 */

import { store } from '@/vuex/store';

/**
 * Generic SSE stream handler
 * @param {string} url - The API endpoint URL
 * @param {Object} body - Request body to send
 * @param {Function} onChunk - Callback for each chunk (receives parsed JSON)
 * @param {Function} onComplete - Callback when stream completes
 * @param {Function} onError - Callback for errors
 */
export async function handleSSEStream(url, body, onChunk, onComplete, onError) {
  try {
    const response = await fetch(url, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'authorization': `Bearer ${store.state.aToken}`,
        'locale': localStorage.getItem("lang") ? localStorage.getItem("lang") : 'hr'
      },
      body: JSON.stringify(body)
    });

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    const reader = response.body.getReader();
    const decoder = new TextDecoder();
    let buffer = '';

    while (true) {
      const { done, value } = await reader.read();
      if (done) break;

      buffer += decoder.decode(value, { stream: true });
      const lines = buffer.split('\n');
      buffer = lines.pop(); // Keep incomplete line in buffer

      for (const line of lines) {
        if (line.startsWith('data: ')) {
          const data = line.slice(6);
          if (data === '[DONE]') {
            if (onComplete) onComplete();
            return;
          }

          try {
            const chunk = JSON.parse(data);
            if (chunk.error) {
              if (onError) onError(new Error(chunk.error));
              return;
            }
            if (onChunk) onChunk(chunk);
          } catch (e) {
            console.error('Failed to parse SSE data:', e);
          }
        }
      }
    }
  } catch (err) {
    console.error('SSE stream error:', err);
    if (onError) onError(err);
    throw err;
  }
}
